import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from './ui/Card';
import { ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
export function MechanismSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="mechanism" className="py-24 bg-slate-50 overflow-hidden" data-id="element-77">
      <div className="container mx-auto px-4" data-id="element-78">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-79">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-80">
            Why One Pathway Isn't Enough
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          delay: 0.1
        }} className="text-xl text-slate-600" data-id="element-81">
            Traditional treatments only address half the problem. Vabysmo is the
            first bispecific antibody designed to target two distinct pathways
            driving retinal disease.
          </motion.p>
        </div>

        <div ref={ref} className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-82">
          {/* Monotherapy Card */}
          <Card className="p-8 relative overflow-hidden group hover:shadow-lg transition-shadow duration-300" data-id="element-83">
            <div className="absolute top-0 left-0 w-full h-1 bg-slate-200" data-id="element-84" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-85">
              Traditional Monotherapy
            </h3>
            <p className="text-slate-500 mb-8" data-id="element-86">Single pathway inhibition</p>

            <div className="h-64 flex items-end justify-center gap-8 mb-8 bg-slate-50 rounded-lg p-4 border border-slate-100" data-id="element-87">
              <div className="flex flex-col items-center gap-2 w-24" data-id="element-88">
                <motion.div className="w-full bg-slate-300 rounded-t-lg relative" initial={{
                height: 0
              }} animate={isInView ? {
                height: '60%'
              } : {
                height: 0
              }} transition={{
                duration: 1,
                delay: 0.2
              }} data-id="element-89">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold text-slate-400" data-id="element-90">
                    BLOCKED
                  </div>
                </motion.div>
                <span className="text-sm font-medium text-slate-600" data-id="element-91">
                  VEGF-A
                </span>
              </div>

              <div className="flex flex-col items-center gap-2 w-24 opacity-50" data-id="element-92">
                <div className="w-full h-full border-2 border-dashed border-red-200 rounded-t-lg relative flex items-center justify-center bg-red-50/50" data-id="element-93">
                  <span className="text-xs font-bold text-red-400 rotate-90 whitespace-nowrap" data-id="element-94">
                    UNCHECKED
                  </span>
                </div>
                <span className="text-sm font-medium text-red-400" data-id="element-95">Ang-2</span>
              </div>
            </div>

            <div className="flex items-center gap-3 text-slate-500 bg-slate-100 p-4 rounded-lg" data-id="element-96">
              <XCircle className="w-5 h-5 text-slate-400" data-id="element-97" />
              <span className="font-medium" data-id="element-98">Temporary suppression</span>
            </div>
          </Card>

          {/* Vabysmo Card */}
          <Card variant="elevated" className="p-8 relative overflow-hidden border-t-4 border-teal-500 transform md:-translate-y-4" data-id="element-99">
            <div className="absolute top-0 right-0 p-4" data-id="element-100">
              <span className="bg-teal-100 text-teal-700 text-xs font-bold px-3 py-1 rounded-full" data-id="element-101">
                DUAL ACTION
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-102">
              Vabysmo (Faricimab)
            </h3>
            <p className="text-teal-600 mb-8 font-medium" data-id="element-103">
              Dual pathway inhibition
            </p>

            <div className="h-64 flex items-end justify-center gap-8 mb-8 bg-gradient-to-b from-teal-50/50 to-transparent rounded-lg p-4 border border-teal-100" data-id="element-104">
              <div className="flex flex-col items-center gap-2 w-24" data-id="element-105">
                <motion.div className="w-full bg-teal-500 rounded-t-lg relative shadow-lg shadow-teal-500/30" initial={{
                height: 0
              }} animate={isInView ? {
                height: '85%'
              } : {
                height: 0
              }} transition={{
                duration: 1,
                delay: 0.4
              }} data-id="element-106">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold text-teal-600" data-id="element-107">
                    BLOCKED
                  </div>
                </motion.div>
                <span className="text-sm font-bold text-slate-700" data-id="element-108">VEGF-A</span>
              </div>

              <div className="flex flex-col items-center gap-2 w-24" data-id="element-109">
                <motion.div className="w-full bg-blue-600 rounded-t-lg relative shadow-lg shadow-blue-600/30" initial={{
                height: 0
              }} animate={isInView ? {
                height: '85%'
              } : {
                height: 0
              }} transition={{
                duration: 1,
                delay: 0.6
              }} data-id="element-110">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-600" data-id="element-111">
                    BLOCKED
                  </div>
                </motion.div>
                <span className="text-sm font-bold text-slate-700" data-id="element-112">Ang-2</span>
              </div>
            </div>

            <div className="flex items-center gap-3 text-teal-900 bg-teal-50 p-4 rounded-lg border border-teal-100" data-id="element-113">
              <CheckCircle2 className="w-5 h-5 text-teal-500" data-id="element-114" />
              <span className="font-bold" data-id="element-115">Sustained stabilization</span>
            </div>
          </Card>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        delay: 0.8
      }} className="max-w-4xl mx-auto mt-16 bg-slate-900 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-116">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/20 to-blue-900/20" data-id="element-117" />
          <h3 className="text-2xl md:text-3xl font-bold text-white relative z-10" data-id="element-118">
            "Single pathway inhibition offers temporary relief.{' '}
            <br className="hidden md:block" data-id="element-119" />
            <span className="text-teal-400" data-id="element-120">
              Dual pathway inhibition offers sustained control.
            </span>
            "
          </h3>
        </motion.div>
      </div>
    </section>;
}